import { bbox } from '@turf/turf';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { MapRef } from 'react-map-gl/mapbox';

import type { PermitMapView } from '~/api/projects2';
import { getCurrentlyVisibleLegendLayerIds } from '~/components2/Map/Widgets/Legend/getCurrentlyVisibleLegendLayerIds';
import layerStore from '~/store2/layers';
import permittingDrawStore from './permittingDrawStore';
import { PERMITTING_VISIBLE_UTILITY_LAYER_IDS } from './permittingEmbedLayers';
import { buildPermittingNorthArrowInnerHtml } from './permittingNorthArrowShared';

/** Set by `PermittingMapSnapshotBridge` so capture can use `getMap().getCanvas()`. */
export const permittingEmbedMapRef: { current: MapRef | null } = { current: null };

/** Cap once the map has already loaded and we are only waiting for the next paint. */
const MAP_REPAINT_TIMEOUT_MS = 1_500;
/** Cap for a map that is still doing its first load — style, sprite, glyphs and tiles. */
const MAP_LOAD_TIMEOUT_MS = 10_000;

/**
 * How long to wait for the map to settle before reading its canvas.
 *
 * A map that has not finished its first load has never painted the basemap, so its preserved
 * drawing buffer is still blank and capturing on the short cap produces an empty map. A cold visit
 * routinely needs several seconds for that first load, while a reload serves it all from cache —
 * which is why reloading the page appeared to fix the capture.
 */
export function getMapPaintTimeoutMs(mapbox: Pick<MapboxMap, 'loaded'> | undefined): number {
  return mapbox?.loaded() ? MAP_REPAINT_TIMEOUT_MS : MAP_LOAD_TIMEOUT_MS;
}

function waitForMapPaint(mapbox: MapboxMap | undefined, timeoutMs?: number): Promise<void> {
  if (!mapbox) {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer: number | undefined;
    const finish = () => {
      if (settled) return;
      settled = true;
      mapbox.off('idle', finish);
      if (timer !== undefined) window.clearTimeout(timer);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    };

    mapbox.triggerRepaint();
    mapbox.once('idle', finish);
    timer = window.setTimeout(finish, timeoutMs ?? getMapPaintTimeoutMs(mapbox));
  });
}

/** Grid the composite is downscaled to before deciding whether it came back blank. */
const BLANK_SAMPLE_SIZE = 32;

/**
 * True when every sampled pixel is identical. A real capture always carries basemap detail or the
 * project outline, so a perfectly uniform frame means the map had not painted when it was read.
 */
export function isUniformSample(pixels: Uint8ClampedArray | number[]): boolean {
  if (pixels.length < 8) return true;
  for (let i = 4; i < pixels.length; i += 4) {
    if (
      pixels[i] !== pixels[0] ||
      pixels[i + 1] !== pixels[1] ||
      pixels[i + 2] !== pixels[2] ||
      pixels[i + 3] !== pixels[3]
    ) {
      return false;
    }
  }
  return true;
}

/** Downscale-and-sample so a full-resolution `getImageData` is never needed. */
function isBlankComposite(source: HTMLCanvasElement): boolean {
  const sample = document.createElement('canvas');
  sample.width = BLANK_SAMPLE_SIZE;
  sample.height = BLANK_SAMPLE_SIZE;
  const ctx = sample.getContext('2d', { willReadFrequently: true });
  // If the sample can't be read, assume the capture is fine rather than discarding a good map.
  if (!ctx) return false;
  try {
    ctx.drawImage(source, 0, 0, source.width, source.height, 0, 0, BLANK_SAMPLE_SIZE, BLANK_SAMPLE_SIZE);
    return isUniformSample(ctx.getImageData(0, 0, BLANK_SAMPLE_SIZE, BLANK_SAMPLE_SIZE).data);
  } catch {
    return false;
  }
}

/**
 * Composite PNG: Mapbox WebGL canvas (from `getCanvas()` when available) plus CivilDraw 2D overlays.
 * Requires `preserveDrawingBuffer` and a non-reused map instance on the embed.
 */
export function getPermittingMapSnapshotDataUrl(): string | null {
  const root = document.getElementById('permitting-map-embed');
  if (!root) return null;

  const rootRect = root.getBoundingClientRect();
  if (rootRect.width === 0 || rootRect.height === 0) return null;

  const dpr = window.devicePixelRatio || 1;
  const outW = Math.max(1, Math.round(rootRect.width * dpr));
  const outH = Math.max(1, Math.round(rootRect.height * dpr));

  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);

  const candidates = Array.from(root.querySelectorAll('canvas')).filter((c): c is HTMLCanvasElement => {
    if (c.width === 0 || c.height === 0) return false;
    const st = getComputedStyle(c);
    if (st.display === 'none' || st.visibility === 'hidden') return false;
    return true;
  });

  const ordered: HTMLCanvasElement[] = [];
  const seen = new Set<HTMLCanvasElement>();

  const mapboxMap = permittingEmbedMapRef.current?.getMap?.();
  const glCanvas = mapboxMap?.getCanvas?.() as HTMLCanvasElement | undefined;
  if (glCanvas && root.contains(glCanvas) && glCanvas.width > 0) {
    ordered.push(glCanvas);
    seen.add(glCanvas);
  } else {
    for (const c of candidates) {
      if (c.classList.contains('mapboxgl-canvas')) {
        ordered.push(c);
        seen.add(c);
        break;
      }
    }
  }

  for (const c of candidates) {
    if (!seen.has(c)) {
      ordered.push(c);
      seen.add(c);
    }
  }

  if (ordered.length === 0) return null;

  for (const canvas of ordered) {
    try {
      const cr = canvas.getBoundingClientRect();
      const dx = (cr.left - rootRect.left) * dpr;
      const dy = (cr.top - rootRect.top) * dpr;
      const dw = cr.width * dpr;
      const dh = cr.height * dpr;
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, dx, dy, dw, dh);
    } catch {
      // Tainted WebGL or read failure
    }
  }

  // Null rather than an empty frame: the print layout then shows its "unavailable" placeholder,
  // which is far better on a permit document than a silently blank map.
  if (isBlankComposite(out)) return null;

  try {
    return out.toDataURL('image/png');
  } catch {
    return null;
  }
}

export async function capturePermittingMapSnapshotAfterPaint(): Promise<string | null> {
  const mapbox = permittingEmbedMapRef.current?.getMap?.();
  await waitForMapPaint(mapbox);
  const captured = getPermittingMapSnapshotDataUrl();
  if (captured) return captured;

  // Blank on the first read: give the map one more short settle window before giving up.
  await waitForMapPaint(mapbox, MAP_REPAINT_TIMEOUT_MS);
  return getPermittingMapSnapshotDataUrl();
}

/** Current permitting embed viewport (center/zoom/bearing/pitch) for saving with the draft. */
export function getPermittingMapView(): PermitMapView | null {
  const mapbox = permittingEmbedMapRef.current?.getMap?.();
  if (!mapbox) return null;
  const center = mapbox.getCenter();
  return {
    center: [center.lng, center.lat],
    zoom: mapbox.getZoom(),
    bearing: mapbox.getBearing(),
    pitch: mapbox.getPitch(),
  };
}

/** Mapbox scale control markup from the permitting embed (for print layout). */
export function getPermittingEmbedScaleSnapshotHtml(): string {
  const root = document.getElementById('permitting-map-embed');
  const el = root?.querySelector('.mapboxgl-ctrl-scale');
  if (!el) return '';
  return el.outerHTML;
}

/** North arrow HTML for print overlay (bearing at capture/print time). */
export function getPermittingEmbedNorthArrowHtml(): string {
  const bearing = permittingEmbedMapRef.current?.getMap?.()?.getBearing() ?? 0;
  return `<div class="permitting-north-arrow-host">${buildPermittingNorthArrowInnerHtml(bearing)}</div>`;
}

/**
 * Permitting TOC order, filtered to utility layers that are currently drawn on the map (same query as
 * the main Legend). Falls back to layer-store visibility if the embed map ref is unavailable.
 */
export function getPermittingLegendLayerIdsForPrint(): string[] {
  const map = permittingEmbedMapRef.current;
  if (map) {
    const onMap = new Set(getCurrentlyVisibleLegendLayerIds(map));
    return PERMITTING_VISIBLE_UTILITY_LAYER_IDS.filter((id) => onMap.has(id));
  }
  return PERMITTING_VISIBLE_UTILITY_LAYER_IDS.filter((id) => layerStore.visibleLayerIds.has(id));
}

/**
 * Pans/zooms the permitting embed to the project bounds with the same padding as initial load
 * (`EmbeddedCivilDrawMap`), and resets bearing/pitch to match a fresh view.
 */
export function resetPermittingEmbedViewport(): void {
  const mapRef = permittingEmbedMapRef.current;
  const proj = permittingDrawStore.project;
  if (!mapRef || proj.isLoading || !('geometry' in proj) || !proj.geometry) return;

  const b = bbox(proj.geometry as unknown as GeoJSON.Feature);
  mapRef.fitBounds([b[0], b[1], b[2], b[3]] as [number, number, number, number], {
    padding: 100,
    duration: 0,
  });
  const mapbox = mapRef.getMap?.();
  mapbox?.setBearing(0);
  mapbox?.setPitch(0);
}
